import {isFeatureEnabled} from '@github-ui/feature-flags'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {
  LabelPickerBase,
  type LabelPickerBaseProps,
  type LabelPickerLabel as LabelPickerBaseLabel,
} from '@github-ui/item-picker-label'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {fetchQuery, graphql, readInlineData, useClientQuery, useFragment, useRelayEnvironment} from 'react-relay'

import type {LabelPickerClientQuery, LabelPickerClientQuery$data} from './__generated__/LabelPickerClientQuery.graphql'
import type {LabelPickerLabel$key} from './__generated__/LabelPickerLabel.graphql'
import type {LabelPickerLabelsPaginated$key} from './__generated__/LabelPickerLabelsPaginated.graphql'
import type {LabelPickerQuery} from './__generated__/LabelPickerQuery.graphql'
import type {LabelPickerSearchQuery, LabelPickerSearchQuery$data} from './__generated__/LabelPickerSearchQuery.graphql'
import {BulkIssuesItemPickerLabelTsqWithRelayWrapper} from './BulkIssuesItemPickerLabelTsqWithRelayWrapper'
import {LabelPickerTsqWithRelayWrapper} from './LabelPickerTsqWithRelayWrapper'
import type {updateItemPickerIssueLabelsBulkMutation$data} from './mutations/__generated__/updateItemPickerIssueLabelsBulkMutation.graphql'
import {commitCreateLabelMutation} from './mutations/create-item-picker-label-mutation'
import {commitUpdateItemPickerIssueLabelsBulkMutation} from './mutations/update-item-picker-issue-labels-bulk-mutation'

//
// LabelPickerBase <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== LabelPicker (exported)
//                                                                              <== BulkItemPickerLabel (exported)

export type LabelPickerLabel = LabelPickerBaseLabel

export type LabelPickerProps = Omit<LabelPickerBaseProps, 'filterItems'> & {
  labelNames?: string[]
}

type BulkItemPickerLabelProps = Omit<LabelPickerProps, 'onSelectionChange' | 'filterItems' | 'labels'> & {
  existingIssueLabels: Map<string, string[]>
  labelAppliedToAll: LabelPickerLabel[]
  connectionIds: {[key: string]: string[]}
} & SharedBulkActionsItemPickerProps

type ItemPickerWrapperProps = Omit<LabelPickerProps, 'labelQueryRef'> & {
  paginatedKey: LabelPickerLabelsPaginated$key | null
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void

  /**
   * Callback to search for labels on the server.
   *
   * Only used when repository has more than VALUES.labelsInitialLoadCount
   *
   * @param query filter query
   * @returns Search results
   */
  searchLabels?: (query: string) => Promise<LabelPickerLabel[]>
  preloadedData?: LabelPickerClientQuery$data['repository'] | null
}

export const LabelPickerGraphqlQuery = graphql`
  query LabelPickerQuery(
    $owner: String!
    $repo: String!
    $shouldQueryByNames: Boolean = false
    $labelNames: String = ""
    $count: Int = 100
  ) {
    repository(owner: $owner, name: $repo) {
      ...LabelPickerLabelsPaginated
      labelsByNames: labels(names: $labelNames, first: $count, orderBy: {field: NAME, direction: ASC})
        @include(if: $shouldQueryByNames) {
        nodes {
          ...LabelPickerLabel
        }
      }
    }
  }
`

const LabelPickerSearchQuery = graphql`
  query LabelPickerSearchQuery($owner: String!, $repo: String!, $query: String, $count: Int!) {
    repository(owner: $owner, name: $repo) {
      labels(query: $query, first: $count) {
        nodes {
          ...LabelPickerLabel
        }
      }
    }
  }
`

export const AssignedLabelFragment = graphql`
  fragment LabelPickerAssignedLabels on Labelable
  @argumentDefinitions(cursor: {type: "String"}, assignedLabelPageSize: {type: "Int", defaultValue: 100})
  @refetchable(queryName: "LabelPickerAssignedLabelsQuery") {
    labels(first: $assignedLabelPageSize, after: $cursor, orderBy: {field: NAME, direction: ASC})
      @connection(key: "LabelPickerLabel_labels") {
      # eslint-disable-next-line relay/unused-fields
      edges {
        node {
          ...LabelPickerLabel
        }
      }
    }
  }
`

const LabelPickerClientQuery = graphql`
  query LabelPickerClientQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      id
      labels {
        # eslint-disable-next-line relay/unused-fields
        totalCount
        nodes {
          ...LabelPickerLabel
        }
      }
    }
  }
`

export const LabelPickerLabelsPaginatedFragment = graphql`
  fragment LabelPickerLabelsPaginated on Repository
  @argumentDefinitions(cursor: {type: "String"}, count: {type: "Int", defaultValue: 100})
  @refetchable(queryName: "LabelPickerLabelsListPaginationQuery") {
    id
    labels(first: $count, after: $cursor, orderBy: {field: NAME, direction: ASC}) {
      nodes {
        ...LabelPickerLabel
      }
      # eslint-disable-next-line relay/unused-fields
      totalCount
    }
  }
`

export const LabelFragment = graphql`
  fragment LabelPickerLabel on Label @inline {
    id
    # eslint-disable-next-line relay/unused-fields
    color
    name
    # eslint-disable-next-line relay/unused-fields
    nameHTML
    # eslint-disable-next-line relay/unused-fields
    description
    # eslint-disable-next-line relay/unused-fields
    url
  }
`

export function BulkIssuesItemPickerLabel(props: BulkItemPickerLabelProps) {
  const isTsqPickerEnabled = isFeatureEnabled('item_picker_label_tsq_migration')

  if (isTsqPickerEnabled) {
    return <BulkIssuesItemPickerLabelTsqWithRelayWrapper {...props} />
  }

  return <BulkIssuesItemPickerLabelRelay {...props} />
}

function BulkIssuesItemPickerLabelRelay({
  issuesToActOn,
  existingIssueLabels,
  labelAppliedToAll,
  connectionIds,
  onCompleted,
  onError,
  ...props
}: BulkItemPickerLabelProps) {
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      const labelsToAdd = selectedLabels.filter(l => !labelAppliedToAll.some(la => la.id === l.id))
      const labelsToRemove = labelAppliedToAll.filter(la => !selectedLabels.some(l => la.id === l.id))
      const args = {
        applyLabelIds: labelsToAdd.map(l => l.id),
        removeLabelIds: labelsToRemove.map(l => l.id),
      }
      const mutationArgs = {
        environment,
        optimisticUpdateIds: issuesToActOn,
        existingIssueLabels,
        connectionIds,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      commitUpdateItemPickerIssueLabelsBulkMutation({
        ...mutationArgs,
        input: {
          ids: [...issuesToActOn],
          ...args,
        },
        onCompleted: ({updateIssuesBulk}: updateItemPickerIssueLabelsBulkMutation$data) => {
          onCompleted?.(updateIssuesBulk?.jobId || undefined)
        },
      })
    },
    [connectionIds, environment, existingIssueLabels, issuesToActOn, labelAppliedToAll, onCompleted, onError],
  )

  return (
    <LabelPicker
      labels={labelAppliedToAll}
      onSelectionChange={onSelectionChange}
      showEditLabelsButton={false}
      {...props}
    />
  )
}

export function LabelPicker(props: LabelPickerProps) {
  const isTsqPickerEnabled = isFeatureEnabled('item_picker_label_tsq_migration')
  return isTsqPickerEnabled ? <LabelPickerTsqWithRelayWrapper {...props} /> : <LabelPickerRelay {...props} />
}

function LabelPickerRelay({anchorElement, showEditLabelsButton, ...rest}: LabelPickerProps) {
  return (
    <LazyItemPicker
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      createChild={() => (
        <ItemPickerFetcher anchorElement={anchorElement} showEditLabelsButton={showEditLabelsButton} {...rest} />
      )}
      insidePortal={rest.insidePortal}
      keybindingCommandId="item-pickers:open-labels"
    />
  )
}

function ItemPickerFetcher({
  repo,
  owner,
  labels,
  labelNames,
  onSelectionChange,
  canCreateLabel,
  ...props
}: LabelPickerProps & {
  canCreateLabel: boolean
}) {
  const environment = useRelayEnvironment()

  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<LabelPickerLabelsPaginated$key | null>(null)
  const [initialLabels, setInitialLabels] = useState<LabelPickerLabel[]>(labels)
  const preloadedData = useClientQuery<LabelPickerClientQuery>(LabelPickerClientQuery, {
    owner,
    repo,
  })
  const hasPreloadedData = Boolean(preloadedData?.repository?.labels?.nodes?.length)
  const [isLoading, setIsLoading] = useState(!hasPreloadedData)

  useEffect(() => {
    if (!IS_SERVER) {
      if (hasPreloadedData) {
        if (labelNames) {
          const labelNamesStrippedQuotes = labelNames.map(name => name.replace(/^"/, '').replace(/"$/, ''))
          const labelsByName = (preloadedData.repository?.labels?.nodes || []).flatMap(node => {
            if (!node) return []
            // eslint-disable-next-line no-restricted-syntax
            const label = readInlineData<LabelPickerLabel$key>(LabelFragment, node)
            return labelNamesStrippedQuotes.includes(label.name) ? [label] : []
          })
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setInitialLabels(labelsByName)
        }
      } else {
        clientSideRelayFetchQueryRetained<LabelPickerQuery>({
          environment,
          query: LabelPickerGraphqlQuery,
          variables: {
            owner,
            repo,
            // Used on issues#index for fetching the labels present in the query string
            shouldQueryByNames: labelNames && labelNames.length > 0,
            labelNames: labelNames?.join(','),
            count: VALUES.labelsInitialLoadCount,
          },
        }).subscribe({
          next: response => {
            const repoLabels = response.repository
            setData(repoLabels ?? null)
            if (labelNames && response.repository?.labelsByNames) {
              const labelsByName = (response.repository?.labelsByNames.nodes || []).flatMap(node =>
                // eslint-disable-next-line no-restricted-syntax
                node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, node)] : [],
              )
              setInitialLabels(labelsByName)
            }
            setIsLoading(false)
            setIsError(false)
          },
          error: () => {
            setIsError(true)
          },
        })
      }
    }
  }, [environment, fetchKey, labelNames, owner, repo, hasPreloadedData, preloadedData])

  const searchLabels = useCallback(
    (query: string) =>
      new Promise<LabelPickerLabel[]>((resolve, reject) => {
        fetchQuery<LabelPickerSearchQuery>(environment, LabelPickerSearchQuery, {
          query,
          owner,
          repo,
          count: VALUES.labelsPageSize,
        }).subscribe({
          next: (fetchedData: LabelPickerSearchQuery$data) => {
            if (fetchedData !== null) {
              const suggestedLabels = (fetchedData.repository?.labels?.nodes || []).flatMap(
                // eslint-disable-next-line no-restricted-syntax
                node => (node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, node)] : []),
              )

              resolve(suggestedLabels)
            }
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      }),
    [environment, owner, repo],
  )

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('labels'),
    anchorElement: anchorProps => props.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  const allLabels = useMemo(() => {
    const a = labelNames ? initialLabels : labels
    if (props.noLabelOption?.selected && !a.find(l => l.id === SPECIAL_VALUES.noLabelsData.id)) {
      a.unshift(SPECIAL_VALUES.noLabelsData as LabelPickerLabel)
    }
    return a
  }, [initialLabels, labelNames, labels, props.noLabelOption?.selected])

  if (isError) {
    return createFallbackComponent(() => setFetchKey(fetchKey + 1))
  }

  return (
    <ItemPickerWrapper
      repo={repo}
      owner={owner}
      paginatedKey={data}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      labels={allLabels}
      canCreateLabel={canCreateLabel}
      onSelectionChange={onSelectionChange}
      searchLabels={searchLabels}
      preloadedData={hasPreloadedData ? preloadedData?.repository : undefined}
      {...props}
    />
  )
}

function ItemPickerWrapper({
  labels,
  copilotSuggestedLabels,
  favoriteLabelIds,
  searchLabels,
  paginatedKey,
  isLoading,
  setIsLoading,
  onSelectionChange,
  insidePortal,
  anchorElement,
  nested = false,
  triggerOpen = true,
  initialFilter,
  showEditLabelsButton = true,
  noLabelOption = undefined,
  title,
  showNoMatchItem = false,
  preloadedData,
  canCreateLabel,
  repo,
  owner,
  readonly,
}: ItemPickerWrapperProps) {
  const data = useFragment(LabelPickerLabelsPaginatedFragment, paginatedKey)
  const repositoryId = preloadedData?.id || data?.id
  const totalNumberOfLabels = preloadedData?.labels?.totalCount || data?.labels?.totalCount || 0
  const fetchedLabels: LabelPickerLabel[] = useMemo(() => {
    const labelItems: LabelPickerLabel[] = (preloadedData?.labels?.nodes || data?.labels?.nodes || []).flatMap(a => {
      if (!a) return []
      // eslint-disable-next-line no-restricted-syntax
      const labelData = readInlineData<LabelPickerLabel$key>(LabelFragment, a)
      return [labelData]
    })

    if (noLabelOption) {
      labelItems.unshift(SPECIAL_VALUES.noLabelsData as LabelPickerLabel)
    }

    return labelItems
  }, [data, noLabelOption, preloadedData])

  const environment = useRelayEnvironment()

  const onCreate = useCallback(
    (
      variables: {repositoryId: string; color: string; name: string},
      options: {onSuccess: (response: LabelPickerLabel | null) => void; onError: () => void},
    ) => {
      if (!repositoryId) return

      commitCreateLabelMutation({
        environment,
        input: variables,
        onError: options.onError,
        onCompleted: response => {
          const newLabel = response?.createLabel?.label
          if (newLabel) {
            options.onSuccess(newLabel as LabelPickerLabel)
          } else {
            options.onSuccess(null)
          }
        },
      })
    },
    [environment, repositoryId],
  )

  return (
    <LabelPickerBase
      labels={labels}
      suggestions={fetchedLabels}
      totalNumberOfLabels={totalNumberOfLabels}
      copilotSuggestedLabels={copilotSuggestedLabels}
      favoriteLabelIds={favoriteLabelIds}
      searchLabels={searchLabels}
      onSelectionChange={onSelectionChange}
      insidePortal={insidePortal}
      anchorElement={anchorElement}
      nested={nested}
      triggerOpen={triggerOpen}
      initialFilter={initialFilter}
      showEditLabelsButton={showEditLabelsButton}
      noLabelOption={noLabelOption}
      title={title}
      showNoMatchItem={showNoMatchItem}
      canCreateLabel={canCreateLabel}
      repositoryId={repositoryId}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      onCreate={onCreate}
      repo={repo}
      owner={owner}
      readonly={readonly}
    />
  )
}

try{ BulkIssuesItemPickerLabel.displayName ||= 'BulkIssuesItemPickerLabel' } catch {}
try{ BulkIssuesItemPickerLabelRelay.displayName ||= 'BulkIssuesItemPickerLabelRelay' } catch {}
try{ LabelPicker.displayName ||= 'LabelPicker' } catch {}
try{ LabelPickerRelay.displayName ||= 'LabelPickerRelay' } catch {}
try{ ItemPickerFetcher.displayName ||= 'ItemPickerFetcher' } catch {}
try{ ItemPickerWrapper.displayName ||= 'ItemPickerWrapper' } catch {}